import api from './client';

/**
 * Short-lived cache for reference lists (statuses, callers, profiles, job categories)
 * that every page used to refetch on mount. Resolves to the same axios response
 * object as `api.get`, so call sites keep reading `res.data`.
 */
const TTL_MS = 60 * 1000;

// key -> { promise, expiresAt } (expiresAt is 0 while the request is in flight)
const entries = new Map();

function cacheKey(url, params) {
  if (!params) return url;
  const parts = Object.keys(params)
    .filter((name) => params[name] !== undefined && params[name] !== null)
    .sort()
    .map((name) => `${name}=${JSON.stringify(params[name])}`);
  return parts.length ? `${url}?${parts.join('&')}` : url;
}

/** GET with a ~60 s cache keyed by URL + params; concurrent callers share one request. */
export function cachedGet(url, config) {
  const key = cacheKey(url, config?.params);
  const hit = entries.get(key);
  if (hit && (hit.expiresAt === 0 || hit.expiresAt > Date.now())) return hit.promise;

  const entry = { promise: null, expiresAt: 0 };
  entry.promise = api.get(url, config).then(
    (res) => {
      // Only start the TTL if this entry was not invalidated while in flight.
      if (entries.get(key) === entry) entry.expiresAt = Date.now() + TTL_MS;
      return res;
    },
    (err) => {
      if (entries.get(key) === entry) entries.delete(key);
      throw err;
    },
  );
  entries.set(key, entry);
  return entry.promise;
}

/** Drop cached entries whose URL starts with `prefix` (everything when omitted). */
export function invalidate(prefix = '') {
  if (!prefix) {
    entries.clear();
    return;
  }
  Array.from(entries.keys()).forEach((key) => {
    if (key.startsWith(prefix)) entries.delete(key);
  });
}

// Any write through the API client may change reference data (directly or via
// derived lists such as "profiles with interviews"), so start fresh after each one.
function isWrite(config) {
  const method = String(config?.method || 'get').toLowerCase();
  return method !== 'get' && method !== 'head' && method !== 'options';
}

api.interceptors.response.use(
  (response) => {
    if (isWrite(response?.config)) invalidate();
    return response;
  },
  (error) => {
    // A failed/timed-out write may still have been applied server-side.
    if (isWrite(error?.config)) invalidate();
    return Promise.reject(error);
  },
);
